import { isEmpty, omit, pipe, trim, uniqBy } from 'lodash/fp'

import { ReviewPrivacy, ReviewType } from 'components/ReviewsWizard/types'
import {
  RecipientSelectorValue,
  TemplateSubmissionFields,
  TemplateFieldsMembers,
  TemplateFormData,
  TemplateFormDataMembers,
} from '../Types'
import {
  RecipientsSelectorValueType,
  ReviewTemplate,
  ReviewEmailMember,
  ReviewMember,
  ReviewMembershipType,
} from 'types'
import {
  isAccessMemberGroupValue,
  isEmailValue,
  isUserOrChannelValue,
} from 'components/ShareSheet/RecipientSelector/Types'

export const emptyTemplate = (): TemplateFormData => ({
  name: '',
  description: '',
  html_description: '',
  days_ahead: 5,
  private: false,
  viewers: [],
  reviewers: [],
  approvers: [],
})

/* === Helpers to convert between form data and API data === */

export const templateToFormData = (template: ReviewTemplate): TemplateFormData => ({
  ...omit(
    [
      'user_viewers',
      'user_reviewers',
      'user_approvers',
      'channel_viewers',
      'channel_reviewers',
      'channel_approvers',
      'privacy',
    ],
    template,
  ),
  ...recipientSelectorValuesFromTemplate(template),
  private: template.privacy === ReviewPrivacy.PRIVATE,
})

export const templateSubmissionFieldsFromFormData = (formData: TemplateFormData): TemplateSubmissionFields => ({
  ...templateMembersFromFormData(formData),
  days_ahead: formData.days_ahead,
  description: trim(formData.description),
  html_description: formData.html_description,
  name: trim(formData.name),
  privacy: formData.private ? ReviewPrivacy.PRIVATE : ReviewPrivacy.PUBLIC,
})

export const templateToSubmissionFields = pipe(templateToFormData, templateSubmissionFieldsFromFormData)

const mapEmailMembers = (type: ReviewMembershipType, values: RecipientSelectorValue[]): ReviewEmailMember[] =>
  values.filter(isEmailValue).map((v) => ({
    membership_type: type,
    email: v.value,
  }))

const mapMembers = (type: ReviewMembershipType, values: RecipientSelectorValue[]): ReviewMember[] =>
  values.filter(isUserOrChannelValue).map((v) => ({
    membership_type: type,
    id: v.value.id,
    type: v.type,
  }))

const mapAccessGroups = (type: ReviewMembershipType, values: RecipientSelectorValue[]): ReviewMember[] =>
  values
    .filter(isAccessMemberGroupValue)
    .flatMap(({ value: { channels, members } }) => [
      ...channels.map((channel) => ({ membership_type: type, id: channel.id, type: 'Channel' as const })),
      ...members.map((user) => ({ membership_type: type, id: user.id, type: 'User' as const })),
    ])

const uniqByIdAndType = uniqBy((member: ReviewMember) => `${member.type}::${member.id}`)
const uniqByEmail = uniqBy((member: ReviewEmailMember) => member.email)

const templateMembersFromFormData = (formData: TemplateFormData): TemplateFieldsMembers => {
  const emailMembers = uniqByEmail([
    ...mapEmailMembers('approver', formData.approvers),
    ...mapEmailMembers('reviewer', formData.reviewers),
    ...mapEmailMembers('viewer', formData.viewers),
  ])

  const members = uniqByIdAndType([
    ...mapMembers('approver', formData.approvers),
    ...mapAccessGroups('approver', formData.approvers),
    ...mapAccessGroups('reviewer', formData.reviewers),
    ...mapMembers('reviewer', formData.reviewers),
    ...mapMembers('viewer', formData.viewers),
    ...mapAccessGroups('viewer', formData.viewers),
  ])

  return {
    members,
    email_members: emailMembers,
  }
}

const formDataMembersFromTemplate = (type: ReviewMembershipType, template: ReviewTemplate) => {
  const channelMembers = template[`channel_${type}s`].map(
    (value): RecipientSelectorValue => ({ type: RecipientsSelectorValueType.Channel, value }),
  )
  const userMembers = template[`user_${type}s`].map(
    (value): RecipientSelectorValue => ({ type: RecipientsSelectorValueType.User, value }),
  )
  return [...channelMembers, ...userMembers]
}

const recipientSelectorValuesFromTemplate = (template: ReviewTemplate): TemplateFormDataMembers => ({
  viewers: formDataMembersFromTemplate('viewer', template),
  reviewers: formDataMembersFromTemplate('reviewer', template),
  approvers: formDataMembersFromTemplate('approver', template),
})

/* === Form Validation === */
export const notEmpty = (value: string) => {
  if (isEmpty(trim(value))) return 'Required'
}

export const isValidDaysAhead = (value: number) => {
  if (value === 0) return 'Required'
  if (!Number.isInteger(value)) return 'Please enter a whole number'
}

/* === Helpers to figure out which type of review this is === */

export const isApprovalForm = (template?: TemplateFormData) => template && template.approvers.length > 0
export const isReadReceiptForm = (template?: TemplateFormData) =>
  template && template.viewers.length > 0 && template.approvers.length === 0 && template.reviewers.length === 0
export const isApprovalTemplate = (template?: ReviewTemplate) =>
  template && (template.user_approvers.length > 0 || template.channel_approvers.length)
export const isReadReceiptTemplate = (template?: ReviewTemplate) => {
  if (!template) return false
  const hasApprovers = template.channel_approvers.length > 0 || template.user_approvers.length > 0
  const hasReviewers = template.channel_reviewers.length > 0 || template.user_reviewers.length > 0
  const hasViewers = template.channel_viewers.length > 0 || template.user_viewers.length > 0
  return !hasReviewers && !hasApprovers && hasViewers
}

export const reviewTypeFromTemplate = (template?: ReviewTemplate) => {
  if (isApprovalTemplate(template)) return ReviewType.APPROVAL
  if (isReadReceiptTemplate(template)) return ReviewType.READ_RECEIPTS
  return ReviewType.FEEDBACK
}

export const reviewTypeFromFormData = (template?: TemplateFormData) => {
  if (isApprovalForm(template)) return ReviewType.APPROVAL
  if (isReadReceiptForm(template)) return ReviewType.READ_RECEIPTS
  return ReviewType.FEEDBACK
}
